import React from "react"
import { OrderStatusesTypes } from "@/types/statuses.types"
import ProgressBarStatusText from "./ProgressBarStatusText"
import StatusStep from "./StatusStep"

interface DeliveryProgressBarProps {
  status: OrderStatusesTypes
  createdAt?: number
  toPickup?: number
  toDropoff?: number
  deliveredAt?: number
  cancelledAt?: number
}

const DeliveryProgressBar: React.FC<DeliveryProgressBarProps> = ({
  status,
  createdAt,
  toPickup,
  toDropoff,
  deliveredAt,
  cancelledAt,
}) => {
  const statusMap = {
    [OrderStatusesTypes.SUBMITTED]: 0,
    [OrderStatusesTypes.CREATED]: 0,
    [OrderStatusesTypes.CANCELLED]: 0,
    [OrderStatusesTypes.TO_PICKUP]: 1,
    [OrderStatusesTypes.AT_HUB]: 2,
    [OrderStatusesTypes.RETURNING]: 2,
    [OrderStatusesTypes.PICKED_UP]: 2,
    [OrderStatusesTypes.TO_DROPOFF]: 2,
    [OrderStatusesTypes.DELIVERED]: 3,
    [OrderStatusesTypes.RETURNED]: 3,
  } as any

  let ToDropoffStepStatus

  switch (status) {
    case OrderStatusesTypes.RETURNING:
      ToDropoffStepStatus = OrderStatusesTypes.RETURNING
      break
    case OrderStatusesTypes.TO_DROPOFF:
    case OrderStatusesTypes.TO_PICKUP:
      ToDropoffStepStatus = OrderStatusesTypes.TO_DROPOFF
      break
    default:
      ToDropoffStepStatus = OrderStatusesTypes.TO_DROPOFF
      break
  }

  const steps = [
    {
      status: OrderStatusesTypes.SUBMITTED,
      activeLink: "/assets/images/status-icons/createdActiveIcon.svg",
      inactiveLink: "/assets/images/status-icons/createdDisabledIcon.svg",
      colSpan: 3,
      colStart: 2,
      eta: createdAt,
    },
    {
      status: OrderStatusesTypes.TO_PICKUP,
      activeLink: "/assets/images/status-icons/toPickupActiveIcon.svg",
      inactiveLink: "/assets/images/status-icons/toPickupDisabledIcon.svg",
      colSpan: 3,
      eta: toPickup,
    },
    {
      status: ToDropoffStepStatus,
      activeLink: "/assets/images/status-icons/toDropoffActiveIcon.svg",
      inactiveLink: "/assets/images/status-icons/toDropoffDisabledIcon.svg",
      colSpan: 3,
      eta: toDropoff,
    },
    {
      status:
        status === OrderStatusesTypes.RETURNED
          ? OrderStatusesTypes.RETURNED
          : OrderStatusesTypes.DELIVERED,
      activeLink: "/assets/images/status-icons/deliveredActiveIcon.svg",
      inactiveLink: "/assets/images/status-icons/deliveredDisabledIcon.svg",
      colSpan: 2,
      hideProgress: true,
      eta: deliveredAt,
      hidden: status === OrderStatusesTypes.CANCELLED,
    },
    // It will replace delivered with cancelled
    {
      status: OrderStatusesTypes.CANCELLED,
      activeLink: "/assets/images/status-icons/cancelledIcon.svg",
      inactiveLink: "/assets/images/status-icons/cancelledIcon.svg",
      hideProgress: true,
      colSpan: 2,
      eta: cancelledAt,
      hidden: status !== OrderStatusesTypes.CANCELLED,
    },
  ]

  const getColSpanClass = (colSpan: number) => {
    switch (colSpan) {
      case 1:
        return "col-span-1"
      case 2:
        return "col-span-2"
      case 3:
        return "col-span-3"
      case 4:
        return "col-span-4"
      default:
        return ""
    }
  }

  const getColStartClass = (colStart: number) => {
    switch (colStart) {
      case 1:
        return "col-start-1"
      case 2:
        return "col-start-2"
      default:
        return ""
    }
  }

  const currentStatusStep = (statusMap[status] as any) || 0
  return (
    <div className="grid grid-cols-12 w-full">
      {steps.map((step, index) => {
        if (step.hidden) {
          return
        }
        const stepCurrentOrCompleted = currentStatusStep >= index
        return (
          <div
            key={index}
            className={`
            ${getColSpanClass(step.colSpan)} ${
              step.colStart ? getColStartClass(step.colStart) : ""
            }`}
          >
            <div className={`grid h-full w-full grid-rows-2 gap-1`}>
              <StatusStep
                stepCompleted={currentStatusStep > index}
                isCurrentStep={index === currentStatusStep}
                hideProgress={step.hideProgress}
                imageLink={stepCurrentOrCompleted ? step.activeLink : step.inactiveLink}
              />
              <ProgressBarStatusText
                style={{ marginLeft: "-76%" }}
                status={step.status}
                eta={step.eta}
                stepCompleted={stepCurrentOrCompleted}
                isCancelled={status === OrderStatusesTypes.CANCELLED}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default DeliveryProgressBar
